import React from 'react';
import { Head, Link } from '@inertiajs/react';
import AdminLayout from '../../../components/AdminLayout';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { ArrowLeft, Edit, Trash2, Calendar, User } from 'lucide-react';

interface BlogPost {
  id: number;
  title: string;
  content: string;
  author?: string;
  published_at?: string;
  status?: string;
  created_at: string;
  updated_at: string;
}

interface Props {
  blog_post: BlogPost;
}

const ShowBlogPost: React.FC<Props> = ({ blog_post }) => {
  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'draft': return 'Brouillon';
      case 'published': return 'Publié';
      case 'archived': return 'Archivé';
      default: return 'Non spécifié';
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      case 'published': return 'bg-green-100 text-green-800';
      case 'archived': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <AdminLayout title={`Article #${blog_post.id}`}>
      <Head title={`${blog_post.title} - Admin`} />
      
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="sm" onClick={() => window.history.back()}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{blog_post.title}</h1>
              <p className="text-gray-600">Article #{blog_post.id}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Link href={`/admin/blog_posts/${blog_post.id}/edit`}>
              <Button variant="outline">
                <Edit className="w-4 h-4 mr-2" />
                Modifier
              </Button>
            </Link>
            <Link 
              href={`/admin/blog_posts/${blog_post.id}`} 
              method="delete" 
              as="button"
              onBefore={() => {
                if (!confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) {
                  return false;
                }
              }}
            >
              <Button variant="outline" className="text-red-600 hover:text-red-800">
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer
              </Button>
            </Link>
          </div>
        </div>

        {/* Article Details */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Informations générales */}
          <Card>
            <CardHeader>
              <CardTitle>Informations générales</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">ID</label>
                <p className="text-lg font-semibold text-gray-900">#{blog_post.id}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Titre</label>
                <p className="text-lg font-semibold text-gray-900">{blog_post.title}</p>
              </div>
              {blog_post.author && (
                <div className="flex items-center space-x-2">
                  <User className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-900">{blog_post.author}</span>
                </div>
              )}
              <div>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(blog_post.status)}`}>
                  {getStatusLabel(blog_post.status)}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Dates */}
          <Card>
            <CardHeader>
              <CardTitle>Dates</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {blog_post.published_at && (
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Publié le</label>
                    <p className="text-sm text-gray-900">
                      {new Date(blog_post.published_at).toLocaleDateString('fr-FR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-500">Créé le</label>
                <p className="text-sm text-gray-900">
                  {new Date(blog_post.created_at).toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Modifié le</label>
                <p className="text-sm text-gray-900">
                  {new Date(blog_post.updated_at).toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Link href="/admin/blog_posts">
                <Button variant="outline" className="w-full">
                  Voir tous les articles
                </Button>
              </Link>
              <Link href="/admin/blog_posts/new">
                <Button className="w-full">
                  Créer un nouvel article
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>

        {/* Contenu */}
        <Card>
          <CardHeader>
            <CardTitle>Contenu de l'article</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose max-w-none">
              <div className="whitespace-pre-wrap text-gray-900 leading-relaxed">
                {blog_post.content}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Statistiques */}
        <Card>
          <CardHeader>
            <CardTitle>Statistiques</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {blog_post.content.length}
                </div>
                <div className="text-sm text-gray-500">Caractères</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {blog_post.content.split(' ').length}
                </div>
                <div className="text-sm text-gray-500">Mots</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {blog_post.content.split('\n').length}
                </div>
                <div className="text-sm text-gray-500">Lignes</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default ShowBlogPost; 
